import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const INITIAL_FEES = [
  { id: 1, name: 'Management Fee', type: 'Management', rate: '1.5%', basis: 'Committed', frequency: 'Annual', recipient: 'SeedDrive GP Ltd', notes: 'On committed capital during investment period' },
  { id: 2, name: 'Transaction Fee', type: 'Transaction', rate: '1.0%', basis: 'Deal Value', frequency: 'One-off', recipient: 'SeedDrive GP Ltd', notes: 'Payable on deal close' },
  { id: 3, name: 'Monitoring Fee', type: 'Monitoring', rate: '£150k p.a.', basis: 'Invested', frequency: 'Annual', recipient: 'SeedDrive GP Ltd', notes: 'Per portfolio company' },
  { id: 4, name: 'Advisory Fee', type: 'Advisory', rate: '0.5%', basis: 'AUM', frequency: 'Annual', recipient: 'Meridian Capital Partners', notes: 'Strategic advisory services' },
  { id: 5, name: 'Exit Fee', type: 'Exit', rate: '1.0%', basis: 'Deal Value', frequency: 'On exit', recipient: 'SeedDrive GP Ltd', notes: 'Payable on realisation' },
];

const J_CURVE_DATA = [
  { year: 0, cashflow: -20 }, { year: 1, cashflow: -35 }, { year: 2, cashflow: -28 },
  { year: 3, cashflow: -12 }, { year: 4, cashflow: 5 }, { year: 5, cashflow: 22 },
  { year: 6, cashflow: 38 }, { year: 7, cashflow: 55 }, { year: 8, cashflow: 72 },
  { year: 9, cashflow: 88 }, { year: 10, cashflow: 110 },
];

const ECONOMICS = [
  { label: 'Gross IRR', value: '16.2%', sub: 'Before fees & carry', color: 'text-emerald-600' },
  { label: 'Net IRR', value: '14.2%', sub: 'After fees & carry', color: 'text-blue-700' },
  { label: 'Net MOIC', value: '1.9x', sub: 'Money-on-Invested-Capital', color: 'text-foreground' },
  { label: 'DPI (proj.)', value: '1.4x', sub: 'Distributions to Paid-In', color: 'text-amber-600' },
  { label: 'TVPI (proj.)', value: '1.9x', sub: 'Total Value to Paid-In', color: 'text-foreground' },
  { label: 'J-curve Inflection', value: 'Year 4', sub: 'Estimated breakeven', color: 'text-indigo-600' },
];

export default function FeesEconomicsTab({ deal, readOnly }) {
  const [fees, setFees] = useState(INITIAL_FEES);

  const removeFee = (id) => {
    if (readOnly) return;
    setFees((prev) => prev?.filter((f) => f?.id !== id));
  };

  const addFee = () => {
    if (readOnly) return;
    setFees((prev) => [...prev, { id: Date.now(), name: 'New Fee', type: 'Management', rate: '0%', basis: 'AUM', frequency: 'Annual', recipient: '', notes: '' }]);
  };

  return (
    <div className="space-y-6">
      {/* Fee Schedule */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <Icon name="Receipt" size={16} color="var(--color-primary)" />
            Fee Schedule
          </h3>
          {!readOnly && (
            <button onClick={addFee} className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 font-medium">
              <Icon name="Plus" size={14} /> Add Fee
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                {['Fee Name', 'Type', 'Rate', 'Basis', 'Frequency', 'Recipient', 'Notes', '']?.map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {fees?.map((fee) => (
                <tr key={fee?.id} className="hover:bg-muted/20">
                  <td className="px-3 py-2 font-medium text-foreground">{fee?.name}</td>
                  <td className="px-3 py-2">
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[rgba(59,76,76,0.07)] text-blue-700">{fee?.type}</span>
                  </td>
                  <td className="px-3 py-2 font-semibold text-foreground">{fee?.rate}</td>
                  <td className="px-3 py-2 text-muted-foreground">{fee?.basis}</td>
                  <td className="px-3 py-2 text-muted-foreground">{fee?.frequency}</td>
                  <td className="px-3 py-2 text-muted-foreground">{fee?.recipient}</td>
                  <td className="px-3 py-2 text-muted-foreground text-xs">{fee?.notes}</td>
                  <td className="px-3 py-2">
                    {!readOnly && (
                      <button onClick={() => removeFee(fee?.id)} className="text-red-400 hover:text-red-600">
                        <Icon name="Trash2" size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Economics Summary */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="TrendingUp" size={16} color="var(--color-primary)" />
          Economics Summary
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {ECONOMICS?.map((e) => (
            <div key={e?.label} className="bg-muted/30 rounded-lg p-4">
              <p className="text-xs text-muted-foreground">{e?.label}</p>
              <p className={`text-2xl font-bold ${e?.color}`}>{e?.value}</p>
              <p className="text-xs text-muted-foreground">{e?.sub}</p>
            </div>
          ))}
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          <Icon name="Info" size={14} className="inline mr-1" />
          Fee drag: Fees reduce gross IRR of 16.2% to net IRR of 14.2% — drag: 2.0 pts
        </div>
      </div>
      {/* J-Curve */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Activity" size={16} color="var(--color-primary)" />
          J-Curve — Cumulative Net Cashflow
        </h3>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={J_CURVE_DATA} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="year" tick={{ fontSize: 11 }} label={{ value: 'Year', position: 'insideBottom', offset: -2, fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} unit="m" />
            <Tooltip formatter={(v) => `£${v}m`} />
            <Area type="monotone" dataKey="cashflow" name="Net Cashflow" stroke="#1e40af" fill="#dbeafe" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
        <p className="text-xs text-muted-foreground mt-2 italic">Sample data — capital calls shown as negative, distributions as positive</p>
      </div>
    </div>
  );
}
